import React, { useState, useEffect, useRef } from 'react';

const sections = [
  {
    id: '01',
    title: 'Data Architecture',
    icon: '⬡',
    tag: 'LOCAL-FIRST',
    content: (
      <>
        <p>SmartLearn AI is built on a <strong>Local-First</strong> philosophy. Your notes, tasks, and study materials live exclusively on your device — never on our servers.</p>
        <div className="highlight-box">
          <span className="highlight-label">Core Principle</span>
          <p>We do not maintain a central database of your personal content. What's yours stays yours.</p>
        </div>
      </>
    ),
  },
  {
    id: '02',
    title: 'AI Tutoring',
    icon: '⬢',
    tag: 'GEMINI 2.5',
    content: (
      <>
        <p>When you use the AI Tutor, your messages are processed via <strong>Google Gemini 2.5 Flash Lite</strong> to generate a response.</p>
        <div className="pill-list">
          <div className="pill">
            <span className="pill-icon">→</span>
            <div><strong>Processing</strong><br/>Data is transmitted only to generate your response.</div>
          </div>
          <div className="pill">
            <span className="pill-icon">→</span>
            <div><strong>No Retention</strong><br/>Chat logs are never stored on our servers.</div>
          </div>
          <div className="pill">
            <span className="pill-icon">→</span>
            <div><strong>Third-Party</strong><br/>Google processes this data under the Google APIs Terms of Service.</div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: '03',
    title: 'Payments & Reports',
    icon: '◈',
    tag: 'SECURE',
    content: (
      <>
        <div className="pill-list">
          <div className="pill">
            <span className="pill-icon">₿</span>
            <div><strong>RevenueCat</strong><br/>Handles all subscription validation. We never see or store your payment details.</div>
          </div>
          <div className="pill">
            <span className="pill-icon">✉</span>
            <div><strong>EmailJS</strong><br/>Bug reports you submit are transmitted via EmailJS directly to our support team.</div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: '04',
    title: 'Your Rights',
    icon: '◎',
    tag: 'FULL CONTROL',
    content: (
      <>
        <p>Because your data stays on your device, you have full control at all times.</p>
        <div className="rights-grid">
          {['Delete your data anytime from device settings', 'Export your content whenever you choose', 'No account required to use core features', 'No tracking or analytics on your study habits'].map((right, i) => (
            <div key={i} className="right-item">
              <span className="right-check">✓</span>
              <span>{right}</span>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: '05',
    title: 'Contact',
    icon: '◐',
    tag: 'SUPPORT',
    content: (
      <>
        <p>Questions about this policy? We're here to help.</p>
        <div className="contact-box">
          <span className="contact-label">Nexias Support</span>
          <a href="mailto:nexiasdev@gmail.com" className="contact-email">nexiasdev@gmail.com</a>
        </div>
      </>
    ),
  },
];

const GlowOrb = ({ x, y, size, color, opacity }) => (
  <div style={{
    position: 'fixed',
    left: x,
    top: y,
    width: size,
    height: size,
    borderRadius: '50%',
    background: color,
    opacity,
    filter: `blur(${size * 0.5}px)`,
    pointerEvents: 'none',
    zIndex: 0,
    transform: 'translate(-50%, -50%)',
  }} />
);

const App = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [visible, setVisible] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const rootRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setVisible(true), 80);
    const handleMove = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg: #05070f;
          --surface: #0b0f1e;
          --surface2: #0f1527;
          --border: rgba(255,255,255,0.06);
          --border-hover: rgba(255,255,255,0.14);
          --text: #c8d3f5;
          --text-dim: #4a5578;
          --text-bright: #e8eeff;
          --accent: #4f7dff;
          --accent2: #a78bfa;
          --accent3: #06b6d4;
          --glow: rgba(79,125,255,0.12);
        }

        html, body { background: var(--bg); }

        .pp-root {
          min-height: 100vh;
          background: var(--bg);
          color: var(--text);
          font-family: 'DM Mono', monospace;
          font-size: 14px;
          line-height: 1.75;
          padding: 0 24px 100px;
          position: relative;
          overflow-x: hidden;
        }

        /* Noise texture overlay */
        .pp-root::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
          opacity: 0.025;
          pointer-events: none;
          z-index: 1;
        }

        /* Grid lines bg */
        .pp-root::after {
          content: '';
          position: fixed;
          inset: 0;
          background-image: 
            linear-gradient(rgba(79,125,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(79,125,255,0.03) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
          z-index: 0;
        }

        .pp-inner {
          max-width: 760px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1), transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .pp-inner.visible { opacity: 1; transform: translateY(0); }

        /* ─── HEADER ─── */
        .pp-header {
          padding: 80px 0 60px;
          position: relative;
        }

        .pp-header-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 48px;
        }

        .pp-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: rgba(79,125,255,0.08);
          border: 1px solid rgba(79,125,255,0.2);
          border-radius: 100px;
          font-family: 'DM Mono', monospace;
          font-size: 10px;
          letter-spacing: 0.18em;
          color: #6b8fff;
          text-transform: uppercase;
        }

        .pp-badge::before {
          content: '';
          width: 6px;
          height: 6px;
          background: #4f7dff;
          border-radius: 50%;
          box-shadow: 0 0 8px #4f7dff;
          animation: pulse 2s ease infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .pp-version {
          font-family: 'DM Mono', monospace;
          font-size: 10px;
          color: var(--text-dim);
          letter-spacing: 0.1em;
        }

        .pp-title-wrap {
          position: relative;
          margin-bottom: 32px;
        }

        .pp-title-bg {
          position: absolute;
          top: -20px;
          left: -40px;
          font-family: 'Syne', sans-serif;
          font-size: clamp(80px, 18vw, 160px);
          font-weight: 800;
          color: rgba(255,255,255,0.015);
          letter-spacing: -0.04em;
          line-height: 1;
          pointer-events: none;
          user-select: none;
          white-space: nowrap;
        }

        .pp-title {
          font-family: 'Syne', sans-serif;
          font-size: clamp(52px, 9vw, 88px);
          font-weight: 800;
          color: var(--text-bright);
          line-height: 0.95;
          letter-spacing: -0.03em;
          position: relative;
        }

        .pp-title-sub {
          display: block;
          background: linear-gradient(135deg, #4f7dff 0%, #a78bfa 50%, #06b6d4 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .pp-header-meta {
          display: flex;
          align-items: center;
          gap: 0;
          border: 1px solid var(--border);
          border-radius: 10px;
          overflow: hidden;
          width: fit-content;
        }

        .pp-meta-item {
          padding: 10px 20px;
          font-size: 11px;
          color: var(--text-dim);
          letter-spacing: 0.06em;
          border-right: 1px solid var(--border);
          position: relative;
        }

        .pp-meta-item:last-child { border-right: none; }

        .pp-meta-item span {
          display: block;
          font-size: 10px;
          color: rgba(255,255,255,0.2);
          letter-spacing: 0.15em;
          text-transform: uppercase;
          margin-bottom: 3px;
        }

        .pp-divider {
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(79,125,255,0.3), rgba(167,139,250,0.3), transparent);
          margin-bottom: 48px;
        }

        /* ─── SECTIONS ─── */
        .pp-sections { display: flex; flex-direction: column; gap: 3px; }

        .pp-section {
          border: 1px solid var(--border);
          border-radius: 14px;
          overflow: hidden;
          transition: border-color 0.25s ease, box-shadow 0.25s ease;
          background: var(--surface);
        }

        .pp-section:hover {
          border-color: var(--border-hover);
          box-shadow: 0 0 0 1px rgba(79,125,255,0.05), 0 8px 32px rgba(0,0,0,0.4);
        }

        .pp-section.open {
          border-color: rgba(79,125,255,0.25);
          box-shadow: 0 0 0 1px rgba(79,125,255,0.08), 0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(79,125,255,0.1);
        }

        .pp-section-header {
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 22px 26px;
          cursor: pointer;
          user-select: none;
          position: relative;
          transition: background 0.2s ease;
        }

        .pp-section.open .pp-section-header {
          background: linear-gradient(90deg, rgba(79,125,255,0.05), transparent);
        }

        .pp-section-num {
          font-family: 'DM Mono', monospace;
          font-size: 10px;
          font-weight: 500;
          color: var(--text-dim);
          letter-spacing: 0.1em;
          min-width: 24px;
          opacity: 0.6;
        }

        .pp-section-icon-wrap {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          background: rgba(255,255,255,0.03);
          border: 1px solid var(--border);
          font-size: 16px;
          color: var(--text-dim);
          transition: all 0.25s ease;
          flex-shrink: 0;
        }

        .pp-section.open .pp-section-icon-wrap {
          background: rgba(79,125,255,0.1);
          border-color: rgba(79,125,255,0.3);
          color: #6b8fff;
          box-shadow: 0 0 16px rgba(79,125,255,0.15);
        }

        .pp-section-title-group { flex: 1; }

        .pp-section-title {
          font-family: 'Syne', sans-serif;
          font-size: 15px;
          font-weight: 700;
          color: var(--text-bright);
          letter-spacing: -0.01em;
          display: block;
        }

        .pp-section-tag {
          font-family: 'DM Mono', monospace;
          font-size: 9px;
          letter-spacing: 0.18em;
          color: var(--text-dim);
          text-transform: uppercase;
          margin-top: 1px;
          display: block;
        }

        .pp-section.open .pp-section-tag { color: rgba(79,125,255,0.6); }

        .pp-section-chevron {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: rgba(255,255,255,0.03);
          border: 1px solid var(--border);
          color: var(--text-dim);
          font-size: 10px;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .pp-section.open .pp-section-chevron {
          transform: rotate(180deg);
          background: rgba(79,125,255,0.1);
          border-color: rgba(79,125,255,0.25);
          color: #6b8fff;
        }

        .pp-section-body {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .pp-section.open .pp-section-body { max-height: 700px; }

        .pp-section-content {
          padding: 0 26px 26px 86px;
          color: var(--text);
          font-size: 13.5px;
        }

        .pp-section-content p { margin-bottom: 14px; color: rgba(200,211,245,0.7); }
        .pp-section-content p:last-child { margin-bottom: 0; }
        .pp-section-content strong { color: var(--text-bright); font-weight: 500; }

        /* ─── INNER COMPONENTS ─── */
        .highlight-box {
          background: rgba(79,125,255,0.04);
          border: 1px solid rgba(79,125,255,0.12);
          border-left: 2px solid rgba(79,125,255,0.5);
          border-radius: 0 10px 10px 0;
          padding: 16px 18px;
          margin-top: 16px;
        }

        .highlight-label {
          font-family: 'DM Mono', monospace;
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(79,125,255,0.7);
          display: block;
          margin-bottom: 8px;
        }

        .highlight-box p { margin-bottom: 0; }

        .pill-list { display: flex; flex-direction: column; gap: 6px; margin-top: 16px; }

        .pill {
          display: flex;
          gap: 14px;
          align-items: flex-start;
          background: rgba(255,255,255,0.02);
          border-radius: 10px;
          padding: 13px 16px;
          border: 1px solid var(--border);
          transition: border-color 0.2s, background 0.2s;
        }

        .pill:hover {
          background: rgba(79,125,255,0.04);
          border-color: rgba(79,125,255,0.15);
        }

        .pill-icon {
          color: rgba(79,125,255,0.5);
          font-size: 13px;
          min-width: 16px;
          margin-top: 2px;
        }

        .pill strong {
          display: block;
          margin-bottom: 3px;
          font-size: 12px;
          color: var(--text-bright);
          font-family: 'Syne', sans-serif;
          font-weight: 700;
        }

        .pill div { font-size: 12.5px; line-height: 1.6; color: rgba(200,211,245,0.6); }

        .rights-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
          margin-top: 16px;
        }

        @media (max-width: 520px) { .rights-grid { grid-template-columns: 1fr; } }

        .right-item {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          font-size: 12.5px;
          padding: 12px 14px;
          background: rgba(255,255,255,0.02);
          border-radius: 10px;
          border: 1px solid var(--border);
          transition: all 0.2s;
        }

        .right-item:hover {
          background: rgba(6,182,212,0.04);
          border-color: rgba(6,182,212,0.15);
        }

        .right-check {
          color: #06b6d4;
          font-size: 11px;
          margin-top: 2px;
          flex-shrink: 0;
          text-shadow: 0 0 8px rgba(6,182,212,0.5);
        }

        .contact-box {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 16px;
          padding: 20px 22px;
          background: rgba(167,139,250,0.04);
          border-radius: 12px;
          border: 1px solid rgba(167,139,250,0.12);
        }

        .contact-label {
          font-family: 'DM Mono', monospace;
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(167,139,250,0.6);
        }

        .contact-email {
          color: var(--text-bright);
          text-decoration: none;
          font-size: 15px;
          font-family: 'Syne', sans-serif;
          font-weight: 600;
          transition: color 0.2s;
          background: linear-gradient(135deg, #4f7dff, #a78bfa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* ─── FOOTER ─── */
        .pp-footer {
          margin-top: 64px;
          padding: 32px 0;
          border-top: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }

        .pp-footer-left { display: flex; flex-direction: column; gap: 4px; }

        .pp-footer-brand {
          font-family: 'Syne', sans-serif;
          font-size: 14px;
          font-weight: 800;
          color: var(--text-bright);
          letter-spacing: -0.01em;
        }

        .pp-footer-sub {
          font-size: 11px;
          color: var(--text-dim);
          letter-spacing: 0.05em;
        }

        .pp-footer-right {
          font-size: 11px;
          color: var(--text-dim);
          text-align: right;
          line-height: 1.8;
        }

        /* Section entrance animation */
        .pp-section {
          animation: fadeUp 0.6s cubic-bezier(0.16,1,0.3,1) both;
        }
        .pp-section:nth-child(1) { animation-delay: 0.1s; }
        .pp-section:nth-child(2) { animation-delay: 0.18s; }
        .pp-section:nth-child(3) { animation-delay: 0.26s; }
        .pp-section:nth-child(4) { animation-delay: 0.34s; }
        .pp-section:nth-child(5) { animation-delay: 0.42s; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Cursor glow effect */
        .cursor-glow {
          position: fixed;
          width: 400px;
          height: 400px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(79,125,255,0.06) 0%, transparent 70%);
          pointer-events: none;
          z-index: 1;
          transform: translate(-50%, -50%);
          transition: left 0.15s ease, top 0.15s ease;
        }
      `}</style>

      <div
        className="cursor-glow"
        style={{ left: mousePos.x, top: mousePos.y }}
      />

      <GlowOrb x="10%" y="15%" size={500} color="radial-gradient(circle, rgba(79,125,255,0.15), transparent)" opacity={0.6} />
      <GlowOrb x="85%" y="60%" size={400} color="radial-gradient(circle, rgba(167,139,250,0.12), transparent)" opacity={0.5} />
      <GlowOrb x="50%" y="85%" size={350} color="radial-gradient(circle, rgba(6,182,212,0.1), transparent)" opacity={0.4} />

      <div className="pp-root" ref={rootRef}>
        <div className={`pp-inner${visible ? ' visible' : ''}`}>

          <header className="pp-header">
            <div className="pp-header-top">
              <div className="pp-badge">SmartLearn AI · Nexias</div>
              <div className="pp-version">V 1.0</div>
            </div>

            <div className="pp-title-wrap">
              <div className="pp-title-bg">PRIV</div>
              <h1 className="pp-title">
                Privacy<br />
                <span className="pp-title-sub">Policy</span>
              </h1>
            </div>

            <div className="pp-header-meta">
              <div className="pp-meta-item">
                <span>Last Updated</span>
                Feb 28, 2026
              </div>
              <div className="pp-meta-item">
                <span>Version</span>
                1.0
              </div>
              <div className="pp-meta-item">
                <span>Storage</span>
                Local-First
              </div>
            </div>
          </header>

          <div className="pp-divider" />

          <div className="pp-sections">
            {sections.map((section) => {
              const isOpen = activeSection === section.id;
              return (
                <div key={section.id} className={`pp-section${isOpen ? ' open' : ''}`}>
                  <div
                    className="pp-section-header"
                    onClick={() => setActiveSection(isOpen ? null : section.id)}
                  >
                    <span className="pp-section-num">{section.id}</span>
                    <div className="pp-section-icon-wrap">{section.icon}</div>
                    <div className="pp-section-title-group">
                      <span className="pp-section-title">{section.title}</span>
                      <span className="pp-section-tag">{section.tag}</span>
                    </div>
                    <div className="pp-section-chevron">▼</div>
                  </div>
                  <div className="pp-section-body">
                    <div className="pp-section-content">{section.content}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <footer className="pp-footer">
            <div className="pp-footer-left">
              <span className="pp-footer-brand">SmartLearn AI</span>
              <span className="pp-footer-sub">Built by Rithish · Nexias</span>
            </div>
            <div className="pp-footer-right">
              © 2026 Rithish. All rights reserved.<br />
              <span style={{ opacity: 0.5 }}>Your data. Your device. Always.</span>
            </div>
          </footer>

        </div>
      </div>
    </>
  );
};

export default App;